import { useEffect, useRef } from 'react';
import useRateCalc from '../../../application/bloc/useRateCalc';

import {
  HeadTitle,
  TextField,
  Spacer,
  SegmentButtons,
  Button,
  Divider,
  ItemContent,
  SpacerSection,
  TheRateCalcTable,
} from 'oui';
import { DefaultBox, SideBox } from '../LBox/LBox';
import {
  currency,
  currencyKR,
  decimalNumber,
} from '../../../application/helper/filter';

interface IRateCalculator {
  onIconClick?: () => void;
  onInfoIconClick?: () => void;
  isBottomSheet?: boolean;
  presetAmount?: string;
  presetInterestRate?: string;
  executeLoan?: () => void;
}

const RateCalculatorAlone = ({
  onIconClick,
  onInfoIconClick,
  isBottomSheet,
  presetAmount = '',
  presetInterestRate = '',
}: IRateCalculator) => {
  const targetRef = useRef<HTMLDivElement>(null);
  const {
    repayment,
    isRepayment,
    inputs,
    setInputs,
    calculatedData,
    result,
    loanAmountWarningText,
    annualInterestWarningText,
    loanTermWarningText,
    handleClickCalculate,
    handleClickTab,
    handleInputChange,
    handleFocusTextField,
    handleBlurTextField,
    handleMouseDown,
    inputRef,
    inputRef2,
    inputRef3,
    sendBeaconClkBtnHelp,
    sendBeaconClkBtnCalc,
    hasAppbar,
  } = useRateCalc();

  const handleClickHelper = () => {
    sendBeaconClkBtnHelp('도움말');
    onInfoIconClick && onInfoIconClick();
  };

  const handleClickMoveToTargetRef = () => {
    setTimeout(() => {
      targetRef.current?.scrollIntoView({
        behavior: 'smooth',
      });
    }, 0);
  };

  const tabsItems = [
    { label: '원리금균등', value: 0 },
    { label: '원금균등', value: 1 },
    { label: '만기일시', value: 2 },
  ];

  useEffect(() => {
    setInputs({
      ...inputs,
      loanAmount: presetAmount,
      annualInterest: presetInterestRate,
    });
  }, []);

  return (
    <>
      <HeadTitle
        textTitle={'대출 이자 계산기'}
        buttonIconName={isBottomSheet ? 'CloseSharp' : undefined}
        onClickButton={onIconClick}
      />
      <div
        style={{
          overflowY: 'scroll',
          height: isBottomSheet ? `100%` : ``,
        }}
      >
        <DefaultBox>
          <TextField
            state={inputs.loanAmount ? 'focused' : 'enabled'}
            adornment={'만 원'}
            helperText={currencyKR(+inputs?.loanAmount * 10000)}
            placeholder={'대출 금액'}
            onChangeTextInput={(value) =>
              handleInputChange(value, 'loanAmount')
            }
            inputMode={'numeric'}
            value={inputs?.loanAmount ? currency(+inputs?.loanAmount) : ''}
            warning={loanAmountWarningText}
            name={'loanAmount'}
            onFocus={handleFocusTextField}
            onBlur={handleBlurTextField}
            ref={inputRef}
          />
          <Spacer size={'xs'} />
          <TextField
            state={inputs.annualInterest ? 'focused' : 'enabled'}
            adornment={'%'}
            placeholder={'연 금리'}
            onChangeTextInput={(value) =>
              handleInputChange(value, 'annualInterest')
            }
            inputMode={'decimal'}
            value={
              inputs?.annualInterest
                ? decimalNumber(inputs?.annualInterest)
                : ''
            }
            warning={annualInterestWarningText}
            name={'annualInterest'}
            onFocus={handleFocusTextField}
            onBlur={handleBlurTextField}
            ref={inputRef2}
          />
          <Spacer size={'xs'} />
          <TextField
            state={inputs.loanTerm ? 'focused' : 'enabled'}
            adornment={'개월'}
            placeholder={'대출 기간'}
            onChangeTextInput={(value) => handleInputChange(value, 'loanTerm')}
            inputMode={'numeric'}
            value={inputs?.loanTerm ? currency(+inputs?.loanTerm) : ''}
            warning={loanTermWarningText}
            name={'loanTerm'}
            onFocus={handleFocusTextField}
            onBlur={handleBlurTextField}
            ref={inputRef3}
          />
        </DefaultBox>
        <HeadTitle
          textTitle={'상환 방식'}
          fixed={true}
          buttonIconName={'InfoOutlined'}
          onClickButton={handleClickHelper}
        />
        <DefaultBox>
          <SegmentButtons
            items={tabsItems}
            activeIdx={repayment}
            onClickButton={handleClickTab}
          />
        </DefaultBox>
        <DefaultBox>
          <Button
            state={
              inputs.loanAmount &&
              inputs.loanAmount !== '0' &&
              inputs.loanTerm &&
              inputs.loanTerm !== '0' &&
              inputs.annualInterest &&
              inputs.annualInterest !== '0' &&
              isRepayment
                ? 'enabled'
                : 'disabled'
            }
            flexible={true}
            configuration={'contained'}
            onClick={() => {
              sendBeaconClkBtnCalc('계산하기');
              handleClickCalculate(
                repayment,
                +inputs.loanAmount,
                +inputs.loanTerm,
                +inputs.annualInterest,
              );
              handleClickMoveToTargetRef();
            }}
            children={'계산하기'}
            isDisabled={
              !(
                inputs.loanAmount &&
                inputs.loanTerm &&
                inputs.annualInterest &&
                isRepayment
              )
            }
            //@ts-ignore
            onMouseDown={handleMouseDown}
          />
        </DefaultBox>
        {!isBottomSheet && hasAppbar && <div ref={targetRef} />}

        <Spacer size={'lg'} />

        {result.totalLoanInterest && result.totalRepaymentAmount && (
          <>
            <Divider type={'contents'} />
            {(isBottomSheet || !hasAppbar) && <div ref={targetRef} />}
            <HeadTitle textTitle={'이자 계산 결과'} />
            <SpacerSection
              size={'xs'}
              children={
                <SideBox>
                  <ItemContent
                    textTitle={result.totalLoanInterest}
                    subTitle={'총 대출 이자'}
                    stress={true}
                  />
                  <Spacer size={'md'} />
                  <ItemContent
                    textTitle={result.totalRepaymentAmount}
                    subTitle={'총 상환 금액'}
                    stress={true}
                  />
                </SideBox>
              }
            />

            <HeadTitle textTitle={'월 상환 금액'} />
            <TheRateCalcTable dataList={calculatedData} />
            {isBottomSheet && <Spacer size={'lg'} />}
            <Spacer size={'lg'} />
          </>
        )}
      </div>
    </>
  );
};

export default RateCalculatorAlone;
